import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ShoppingCart, Trash2, AlertCircle } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { MAX_BOOK_QUANTITY, MAX_ONLINE_QUANTITY, previewCart, useCart, type CartKind, type CartLine, type CartPreview } from '@/services/cartService'

// หน้าตะกร้า (ไม่มีคูปอง — ใส่คูปองที่หน้าชำระเงิน) — แยก 2 แท็บ: คอร์สออนไลน์ / หนังสือ ชำระเงินแยกกันตามแท็บ (Onsite ไม่มีตะกร้า)
// ตะกร้าเก็บที่ server ตามบัญชี — ทุกครั้งที่เปิด/แก้ไข ตรวจราคา สต๊อก และกติกากับ API ใหม่ (POST /api/cart/preview)
// บัญชีบริษัท: คอร์สออนไลน์ซื้อให้พนักงาน → "จำนวนพนักงาน" ปรับได้ที่นี่ แล้วกรอกอีเมลพนักงานต่อคอร์สในหน้าชำระเงิน
const TABS: { kind: CartKind; label: string }[] = [
  { kind: 'online', label: 'คอร์สออนไลน์' },
  { kind: 'book', label: 'หนังสือ' },
]

const lineHref = (l: CartLine) => (!l.slug ? '#' : l.kind === 'book' ? `/books/${l.slug}` : `/courses/${l.slug}`)

function QuantityStepper({ label, value, max, onChange }: { label: string; value: number; max: number; onChange: (n: number) => void }) {
  return (
    <div className="mt-3 flex items-center gap-3 text-sm">
      <span className="text-text-muted">{label}</span>
      <div className="inline-flex items-center rounded-sm border border-border">
        <button onClick={() => onChange(Math.max(1, value - 1))} className="h-8 w-8 text-text-muted hover:text-accent" aria-label="ลดจำนวน">−</button>
        <span className="w-10 text-center font-semibold text-text">{value}</span>
        <button onClick={() => onChange(Math.min(max, value + 1))} className="h-8 w-8 text-text-muted hover:text-accent" aria-label="เพิ่มจำนวน">+</button>
      </div>
    </div>
  )
}

export default function CartPage() {
  const navigate = useNavigate()
  const isCompany = useAuthStore((s) => s.customerType) === 'COMPANY'
  const cart = useCart()
  const [tab, setTab] = useState<CartKind>(cart.online.length === 0 && cart.books.length > 0 ? 'book' : 'online')
  const [cartError, setCartError] = useState('')

  const lines = tab === 'online' ? cart.online : cart.books
  // แก้จำนวน/ลบ — server ปฏิเสธ (เช่น หนังสือเกินสต๊อก) จะย้อนค่าเดิมพร้อมแจ้งเหตุผล
  const run = (p: Promise<{ ok: boolean; error?: string }>) => { void p.then((r) => setCartError(r.ok ? '' : r.error ?? 'ทำรายการไม่สำเร็จ')) }

  // ผลตรวจ: เก็บคู่กับ "กุญแจคำขอ" — กำลังโหลด = ผลที่ถืออยู่ยังเป็นของคำขอเก่า (ไม่ต้อง setState แบบสั่งโหลดใน effect)
  const requestKey = useMemo(
    () => JSON.stringify([tab, lines.map((l) => [l.refId, l.quantity])]),
    [tab, lines],
  )
  const [result, setResult] = useState<{ key: string; preview: CartPreview | null; error: string } | null>(null)
  const loading = lines.length > 0 && result?.key !== requestKey
  const preview = result?.key === requestKey ? result.preview : null
  const loadError = result?.key === requestKey ? result.error : ''

  useEffect(() => {
    if (lines.length === 0) return
    let cancelled = false
    previewCart(lines, null)
      .then((p) => { if (!cancelled) setResult({ key: requestKey, preview: p, error: '' }) })
      .catch((e: unknown) => { if (!cancelled) setResult({ key: requestKey, preview: null, error: e instanceof Error ? e.message : 'ตรวจสอบตะกร้าไม่สำเร็จ' }) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey])

  const lineInfo = (l: CartLine) => preview?.items.find((p) => p.ref_id === l.refId)
  const hasBadLine = !!preview && preview.items.some((p) => !p.ok)
  const canCheckout = !!preview && preview.can_checkout && !loading && lines.length > 0

  const checkout = () => navigate(`/checkout/${tab === 'online' ? 'online' : 'books'}`)

  return (
    <div className="min-h-screen bg-[#FAFAFB] pb-16 pt-24">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <h1 className="mb-1 font-display text-2xl font-bold text-text">ตะกร้าสินค้า</h1>
        <p className="mb-6 text-sm text-text-muted">เลือกตะกร้าที่ต้องการชำระเงิน — คอร์สออนไลน์และหนังสือชำระแยกกัน</p>

        <div className="mb-6 flex gap-2 border-b border-border">
          {TABS.map((t) => {
            const n = t.kind === 'online' ? cart.online.length : cart.books.length
            return (
              <button
                key={t.kind}
                onClick={() => setTab(t.kind)}
                className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${tab === t.kind ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-text-muted hover:text-text'}`}
              >
                {t.label} <span className="ml-1 rounded-full bg-surface-2 px-2 py-0.5 text-xs">{n}</span>
              </button>
            )
          })}
        </div>

        {cartError && <p className="mb-4 rounded-sm border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-600">{cartError}</p>}

        {!cart.loaded ? (
          <div className="rounded-sm border border-border bg-surface p-12 text-center text-sm text-text-muted">กำลังโหลดตะกร้า...</div>
        ) : lines.length === 0 ? (
          <div className="rounded-sm border border-border bg-surface p-12 text-center">
            <ShoppingCart className="mx-auto h-10 w-10 text-text-muted/50" strokeWidth={1.5} />
            <p className="mt-3 text-sm text-text-muted">{tab === 'online' ? 'ยังไม่มีคอร์สในตะกร้า' : 'ยังไม่มีหนังสือในตะกร้า'}</p>
            <Link to={tab === 'online' ? '/courses' : '/books'} className="mt-4 inline-block rounded-full bg-indigo-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-violet-400">
              {tab === 'online' ? 'เลือกดูคอร์ส' : 'เลือกดูหนังสือ'}
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px]">
            <ul className="space-y-3">
              {lines.map((l) => {
                const info = lineInfo(l)
                const unit = info?.unit_price ?? l.price
                return (
                  <li key={`${l.kind}-${l.refId}`} className={`flex gap-4 rounded-sm border bg-surface p-4 shadow-sm ${info && !info.ok ? 'border-rose-300' : 'border-border'}`}>
                    <Link to={lineHref(l)} className="h-20 w-20 shrink-0 overflow-hidden rounded-sm border border-border bg-surface-2">
                      {l.image ? <img src={l.image} alt="" className="h-full w-full object-cover" /> : null}
                    </Link>
                    <div className="min-w-0 flex-1">
                      <Link to={lineHref(l)} className="font-semibold leading-snug text-text hover:text-accent">{l.title}</Link>
                      {l.subtitle && <p className="mt-0.5 text-xs text-text-muted">{l.subtitle}</p>}
                      {info && !info.ok && (
                        <p className="mt-2 flex items-start gap-1.5 text-xs text-rose-500"><AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{info.error}</p>
                      )}
                      {l.kind === 'book' && (
                        <QuantityStepper label="จำนวน" value={l.quantity} max={MAX_BOOK_QUANTITY} onChange={(n) => run(cart.setQuantity('book', l.refId, n))} />
                      )}
                      {l.kind === 'online' && isCompany && (
                        <QuantityStepper label="จำนวนพนักงาน" value={l.quantity} max={MAX_ONLINE_QUANTITY} onChange={(n) => run(cart.setQuantity('online', l.refId, n))} />
                      )}
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <div className="text-right">
                        <p className="font-bold text-text">{info?.is_free ? 'ฟรี' : `฿${(info?.subtotal ?? unit * l.quantity).toLocaleString()}`}</p>
                        {l.quantity > 1 && !info?.is_free && <p className="text-xs text-text-muted">฿{unit.toLocaleString()} × {l.quantity}</p>}
                      </div>
                      <button onClick={() => run(cart.remove(l.kind, l.refId))} className="flex items-center gap-1 text-xs text-text-muted hover:text-rose-500">
                        <Trash2 className="h-3.5 w-3.5" /> ลบ
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>

            <div>
              <div className="sticky top-24 overflow-hidden rounded-sm border border-border bg-surface shadow-sm">
                <div className="border-b border-border bg-indigo-500/10 px-5 py-3 font-semibold text-text">สรุปรายการ ({lines.length})</div>
                <div className="p-5 text-sm">
                  {loadError && <p className="mb-3 text-xs text-rose-500">{loadError}</p>}
                  {preview?.cart_error && <p className="mb-3 text-xs text-rose-500">{preview.cart_error}</p>}

                  <div className="mb-2 flex justify-between text-text-muted">
                    <span>ยอดรวม</span><span>{loading ? '...' : `฿${(preview?.subtotal ?? 0).toLocaleString()}`}</span>
                  </div>

                  <div className="my-3 border-t border-dashed border-border" />
                  <div className="mb-4 flex justify-between font-bold text-text">
                    <span>สรุปยอดชำระ</span>
                    <span>{loading ? '...' : (preview && preview.final_amount <= 0 ? 'ฟรี' : `฿${(preview?.final_amount ?? 0).toLocaleString()}`)}</span>
                  </div>
                  {hasBadLine && <p className="mb-3 text-xs text-rose-500">มีรายการที่สั่งซื้อไม่ได้ กรุณาลบรายการนั้นก่อนชำระเงิน</p>}
                  <button
                    onClick={checkout}
                    disabled={!canCheckout}
                    className="flex w-full items-center justify-center rounded-full bg-indigo-500 py-3 font-semibold text-white transition hover:bg-violet-400 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    ดำเนินการชำระเงิน
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
